import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTexte } from '../../texte'
import { createPortal } from 'react-dom'

import { Emojiwahl } from './Emojiwahl'

/**
 * Was man mit einer Nachricht tun kann — im Menü statt in der Blase.
 *
 * **Der Grund steht in #8.** In jeder Sprechblase standen vier Symbole
 * dauerhaft da: antworten, auf die Karte, ändern, löschen. In iMessage steht
 * in der Blase nichts als der Text; alles andere liegt im Menü beim langen
 * Drücken. Das war nach der Farbe der auffälligste Unterschied.
 *
 * **Der Preis gehört genannt:** ein Menü, das man erst aufrufen muss, ist
 * schwerer zu finden als vier Symbole, die dastehen. Dafür ist die Blase
 * wieder das, was sie sein soll — Gesagtes und nicht eine Werkzeugleiste.
 *
 * **Drei Wege hinein, weil es drei Geräte gibt:** langes Drücken am Telefon,
 * die rechte Maustaste am Rechner, und die Eingabetaste auf der Blase für
 * die, die mit der Tastatur arbeiten. Ohne den dritten gäbe es gar keinen.
 */
export interface Menueeintrag {
  name: string
  symbol: ReactNode
  /** Löschen trägt `--danger` und steht unten, abgesetzt. */
  gefahr?: boolean
  /**
   * A name for the test bench (`data-menue`), never for a person: a probe
   * that held on to the wording would break at the first rephrasing.
   */
  schluessel?: string
  tun: () => void
}

/**
 * Woran das Menü hängt, in Fensterkoordinaten.
 *
 * **Der Kasten der Blase und nicht der Finger** (aus dem Betrieb: „ja, an der
 * Blase verankern"). Vorher lag das Menü dort, wo der Daumen aufsetzte, und
 * damit **über der Nachricht, um die es geht** — man tippte „Löschen", ohne
 * zu sehen, welche es trifft. iMessage lässt die Blase zwischen der
 * Emoji-Reihe und dem Menü stehen; das geht nur, wenn beides an ihrem Kasten
 * hängt.
 *
 * `x` bleibt daneben stehen: waagerecht richtet sich das Menü weiterhin nach
 * dem Griff, damit es an einer breiten Blase dort aufgeht, wo man hinsieht.
 */
export interface Menueort {
  x: number
  y: number
  /** Oberkante und Unterkante der Blase — daran hängen Reihe und Menü. */
  oben: number
  unten: number
}

/** Wo Reihe und Menü stehen, jedes für sich — sie hängen nicht mehr zusammen. */
interface Platzierung {
  reihe: { left: number; top: number }
  menue: { left: number; top: number }
}

/** Wie lange ein Druck dauern muss, damit er ein langer ist. */
const LANG_MS = 450

/**
 * Wie weit der Finger dabei wandern darf.
 *
 * **Ohne diese Grenze wäre jedes Scrollen ein Menü.** Wer die Liste mit dem
 * Daumen hochschiebt, hält dabei eine halbe Sekunde lang die Blase — und
 * genau das ist der Griff, der hier gemeint ist.
 */
const WACKELN_PX = 10

/**
 * Die Griffe für eine Blase: langes Drücken, rechte Maustaste, Tastatur.
 *
 * Gibt zurück, was an das Element gehängt wird. Der Zustand liegt beim
 * Aufrufer — er weiß, ob es überhaupt etwas zu tun gibt.
 */
export function useLangdruck(oeffne: (ort: Menueort) => void) {
  // **Ein `useRef` und kein `useState`.** Der Griff wird nirgends gezeichnet
  // und nur im nächsten Ereignis gebraucht; als Zustand hinge er daran, dass
  // zwischen Aufsetzen und Loslassen neu gezeichnet wurde.
  const uhr = useRef<number | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  // Das Element, an dem der Griff hängt -- in der Zeitschaltung ist
  // `event.currentTarget` längst `null`, React räumt es vorher weg.
  const blase = useRef<HTMLElement | null>(null)

  /**
   * Der Kasten der Blase, im Augenblick des Öffnens gemessen.
   *
   * **Nicht der der Zeile.** `.bubble-row` ist bildschirmbreit; ihre Mitte
   * ist die Bildmitte, und eine eigene Nachricht steht rechts darin. Als
   * Anker taugt nur die Blase selbst -- oder bei einer Nachricht ohne Text
   * der Kasten ihrer Anhänge.
   */
  function ort(x: number, y: number): Menueort {
    const zeile = blase.current
    const kern = zeile?.querySelector('.bubble, .anhang') ?? zeile
    const kasten = kern?.getBoundingClientRect()
    return {
      x: kasten === undefined ? x : kasten.left + kasten.width / 2,
      y,
      oben: kasten?.top ?? y,
      unten: kasten?.bottom ?? y,
    }
  }

  function ab() {
    if (uhr.current !== null) window.clearTimeout(uhr.current)
    uhr.current = null
    start.current = null
    blase.current = null
  }

  useEffect(() => ab, [])

  return {
    onPointerDown(event: React.PointerEvent) {
      // Nur der Finger und nur der linke Knopf: die rechte Maustaste hat
      // ihren eigenen Weg, und ein zweiter Zähler daneben führte dazu, dass
      // das Menü zweimal aufging.
      if (event.pointerType === 'mouse' && event.button !== 0) return
      start.current = { x: event.clientX, y: event.clientY }
      blase.current = event.currentTarget as HTMLElement
      uhr.current = window.setTimeout(() => {
        const gedrueckt = start.current
        const wo = gedrueckt === null ? null : ort(gedrueckt.x, gedrueckt.y)
        ab()
        if (wo !== null) oeffne(wo)
      }, LANG_MS)
    },
    onPointerMove(event: React.PointerEvent) {
      const ort = start.current
      if (ort === null) return
      if (Math.abs(event.clientX - ort.x) > WACKELN_PX
        || Math.abs(event.clientY - ort.y) > WACKELN_PX) ab()
    },
    onPointerUp: ab,
    onPointerCancel: ab,
    onContextMenu(event: React.MouseEvent) {
      event.preventDefault()
      blase.current = event.currentTarget as HTMLElement
      const wo = ort(event.clientX, event.clientY)
      ab()
      oeffne(wo)
    },
    onKeyDown(event: React.KeyboardEvent) {
      // `ContextMenu` ist die Taste, die es dafür gibt; Eingabe und
      // Leertaste sind das, was man ohne sie versucht.
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ContextMenu') return
      // Nicht, wenn der Griff einem Knopf in der Blase gilt -- ein Zitat, ein
      // Foto, ein Kärtchen haben ihre eigene Bedeutung für die Eingabetaste.
      if (event.target !== event.currentTarget) return
      event.preventDefault()
      const kasten = (event.currentTarget as HTMLElement).getBoundingClientRect()
      oeffne({
        x: kasten.left + kasten.width / 2,
        y: kasten.top + kasten.height / 2,
        oben: kasten.top,
        unten: kasten.bottom,
      })
    },
  }
}

/**
 * Die Emoji-Reihe über dem Menü.
 *
 * Aus dem Betrieb, mit einem Bildschirmfoto aus iMessage: „sowohl bei
 * WhatsApp und iMessage wird zusätzlich die Emoji-Auswahl angezeigt". Dort
 * liegt sie als Pille **über** der Blase, das Menü darunter — beides
 * erscheint mit demselben Griff.
 *
 * **Sie ersetzt den Smiley-Knopf in der Blase.** Der saß als helle Scheibe
 * mitten auf der blauen Fläche und war das Lauteste darin. Was schon
 * vergeben ist, steht weiterhin an der Blase: das ist Inhalt, kein
 * Bedienelement.
 */
export interface Reaktionswahl {
  emojis: readonly string[]
  /** Was ich selbst schon vergeben habe — die Kachel steht dann hervorgehoben. */
  eigene: string[]
  /** Ohne Netz oder in einer abgeschlossenen Reise: abgeschaltet, mit Grund. */
  gesperrt: boolean
  hinweis?: string
  waehle: (emoji: string) => void
}

export function Blasenmenue({
  eintraege,
  reaktionen,
  ort,
  onSchliessen,
}: {
  eintraege: Menueeintrag[]
  /** Ohne Angabe steht nur das Menü da — am Foto und am Highlight etwa. */
  reaktionen?: Reaktionswahl
  ort: Menueort
  onSchliessen: () => void
}) {
  const reiheRef = useRef<HTMLDivElement>(null)
  const t = useTexte()
  const menueRef = useRef<HTMLDivElement>(null)
  const [platz, setPlatz] = useState<Platzierung | null>(null)
  /**
   * Die volle Auswahl statt der Achterreihe.
   *
   * Aus dem Betrieb: „in der Smiley-Ansicht fehlt jetzt das plus für weitere
   * emojis aus der Emoji Tastatur." Sie stand vorher an der Sprechblase und
   * blieb beim Umzug ins Menü zurück.
   */
  const [alle, setAlle] = useState(false)

  /**
   * Erst messen, dann setzen — und die Blase bleibt dabei sichtbar.
   *
   * **Der Regelfall:** die Emoji-Reihe über der Blase, das Menü darunter.
   * Dazwischen steht die Nachricht, um die es geht — man sieht, was man
   * löscht. Genau so hält es iMessage.
   *
   * **Der Rückfall, wenn oben oder unten kein Platz ist:** beides wandert auf
   * dieselbe Seite, gestapelt. Reicht auch das nicht — eine sehr hohe Blase,
   * ein kleines Fenster, die volle Emoji-Auswahl —, wird geklemmt, damit
   * nichts aus dem Bild ragt. Ein Menü, das man nicht ganz sieht, ist keins.
   *
   * Gemessen wird der Kasten, nicht geschätzt: wohin etwas passt, weiß man
   * erst, wenn es steht.
   */
  useEffect(() => {
    const reihe = reiheRef.current?.getBoundingClientRect()
    const menue = menueRef.current?.getBoundingClientRect()
    if (menue === undefined) return

    const rand = 8
    const luft = 8
    const hoch = window.innerHeight
    const breit = window.innerWidth

    // Mittig zur Blase, und notfalls an den Rand geklemmt. `ort.x` ist die
    // Mitte der Blase und nicht mehr die Stelle des Fingers.
    const inDieBreite = (kasten: DOMRect | undefined) =>
      kasten === undefined
        ? rand
        : Math.max(rand, Math.min(ort.x - kasten.width / 2, breit - kasten.width - rand))

    const reihenhoehe = reihe === undefined ? 0 : reihe.height
    const zwischen = reihenhoehe > 0 ? 4 : 0
    const passtOben = ort.oben - luft - reihenhoehe >= rand
    const passtUnten = ort.unten + luft + menue.height <= hoch - rand

    let oben: number
    let unten: number
    if (passtOben && passtUnten) {
      // Der Regelfall: die Blase bleibt frei dazwischen.
      oben = ort.oben - luft - reihenhoehe
      unten = ort.unten + luft
    } else {
      /*
       * **Gestapelt wird als ein Block, nicht als zwei Kästen** (1.67.2).
       *
       * Aus dem Betrieb, mit Bildschirmfoto: bei einer langen Nachricht lagen
       * Emoji-Reihe und Menü übereinander. Der Grund stand in zwei Zeilen, die
       * unabhängig voneinander klemmten: `unten = max(rand, oben - luft -
       * hoehe)` wurde bei einer hohen Blase auf `rand` gezogen, und `oben =
       * max(rand, unten - reihenhoehe - 4)` danach **auch** auf `rand`. Beide
       * standen damit auf 8, also genau aufeinander. Die Klemmung am Ende
       * rechnete jeden Kasten wieder für sich und zog sie nicht auseinander.
       *
       * Jetzt wird nur **eine** Zahl geklemmt -- die Oberkante des Menüs --,
       * und die Reihe hängt daran. Dass sie sich nicht überlagern, ist damit
       * keine Folge der Rechnung mehr, sondern ihre Bauart.
       *
       * **Und das Menü hat Vorrang**, wenn beides zusammen nicht ins Fenster
       * passt: eine halb sichtbare Emoji-Reihe kostet ein Zeichen, ein halb
       * sichtbares Menü kostet „Löschen".
       */
      const stapel = reihenhoehe + zwischen + menue.height
      if (stapel <= hoch - 2 * rand) {
        // Der Block passt ins Fenster: **eine** Zahl klemmen, die Oberkante
        // der Reihe, und das Menü hängt daran.
        const gewuenscht = passtUnten ? ort.unten + luft : ort.oben - luft - stapel
        oben = Math.min(Math.max(rand, gewuenscht), hoch - stapel - rand)
        unten = oben + reihenhoehe + zwischen
      } else {
        // Passt er nicht -- die volle Emoji-Auswahl in einem sehr kleinen
        // Fenster --, hat das Menü Vorrang: eine halb sichtbare Emoji-Reihe
        // kostet ein Zeichen, ein halb sichtbares Menü kostet „Löschen".
        unten = Math.min(Math.max(rand, ort.oben - luft - menue.height), hoch - menue.height - rand)
        oben = unten - zwischen - reihenhoehe
      }
    }

    setPlatz({
      reihe: { left: inDieBreite(reihe), top: oben },
      menue: { left: inDieBreite(menue), top: unten },
    })
  }, [ort, alle])

  useEffect(() => {
    function beiTaste(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onSchliessen()
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [onSchliessen])

  return createPortal(
    <>
      {/* **Der Schleier schließt selbst.**
       *
       * Vorher hing das an einem `pointerdown`-Zuhörer am `window`, der jeden
       * Griff außerhalb des Kastens als Abbruch nahm. Aus dem Betrieb kam,
       * dass ein Tipp daneben nichts tat -- solange iOS eine Textauswahl
       * hielt, kam der Tipp gar nicht bis zu uns. Die Auswahl ist inzwischen
       * abgestellt (siehe `app.css`), aber der Umweg über das Fenster bleibt
       * einer: der Schleier deckt ohnehin das ganze Bild, also **ist** ein
       * Griff darauf der Griff daneben. Kein Ereignisweg, der schiefgehen
       * kann, und keine zweite Regel für dieselbe Sache.
       *
       * Er fängt damit zugleich ab, was darunter liegt: ein Tipp neben ein
       * offenes Menü soll es schließen und nicht noch etwas auslösen.
       *
       * **Und `stopPropagation` ist hier keine Zierde.** Das Blatt hängt an
       * `document.body`, aber Reacts Ereignisse steigen den **React**-Baum
       * hinauf -- und dort liegt die Sprechblase darüber. Ohne die Sperre
       * kam der Tipp auf den Schleier bei `useLangdruck` an, startete einen
       * neuen langen Druck und öffnete das Menü nach 450 ms wieder, an der
       * neuen Stelle. Aus dem Betrieb genau so gemeldet: „legt sich direkt
       * über die Nachricht und wird weiter angezeigt". Dieselbe Falle wie
       * seinerzeit bei der Foto-Großansicht, und sie steht deshalb in
       * `CLAUDE.md`. */}
      <div
        className="blasenmenue__schleier"
        onPointerDown={(event) => {
          event.stopPropagation()
          onSchliessen()
        }}
      />
      {/* **Die Emoji-Reihe steht über der Blase**, das Menü darunter — und
          dazwischen bleibt die Nachricht sichtbar. Beide sind eigene feste
          Kästen: als ein Stück konnten sie nur zusammen oben oder zusammen
          unten liegen, und dann lag eines von beiden über der Nachricht. */}
      {reaktionen !== undefined && !alle && (
        <div
          ref={reiheRef}
          className="blasenmenue__emojis"
          role="group"
          aria-label={t.chat.reagierenName}
          style={{
            left: platz?.reihe.left ?? -9999,
            top: platz?.reihe.top ?? -9999,
            visibility: platz === null ? 'hidden' : 'visible',
          }}
        >
          {/* **Sieben und das ＋, nicht acht und das ＋.** Eine Reihe aus
              44-Punkt-Flächen ist nach acht Stück breiter als ein iPhone --
              mit neun waren es 404 Punkte auf 390, und die erste Kachel lag
              außerhalb. Das achte Zeichen ist nicht weg, es steht einen Tipp
              weiter in der vollen Auswahl. Die Liste selbst bleibt, wie sie
              ist: was der Server annimmt, entscheidet nicht die Breite eines
              Telefons. */}
          {/* Und der Grund steht da, nicht nur im `title` (Codereview E3):
              auf dem Telefon ist ein Tooltip unsichtbar, und die Reihe wäre
              grau ohne ein Wort dazu. */}
          {reaktionen.hinweis !== undefined && (
            <span className="blasenmenue__hinweis" role="status">{reaktionen.hinweis}</span>
          )}
          {reaktionen.emojis.slice(0, 7).map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={
                reaktionen.eigene.includes(emoji)
                  ? 'blasenmenue__emoji blasenmenue__emoji--meins'
                  : 'blasenmenue__emoji'
              }
              disabled={reaktionen.gesperrt}
              title={reaktionen.hinweis}
              onClick={() => {
                onSchliessen()
                reaktionen.waehle(emoji)
              }}
            >
              <span aria-hidden="true">{emoji}</span>
              <span className="visually-hidden">{emoji}</span>
            </button>
          ))}

          {/* **Der Weg zu allem anderen.** Er stand an der Sprechblase und
              blieb beim Umzug ins Menü zurück; aus dem Betrieb gemeldet.
              Dahinter liegt dieselbe Auswahl wie am Foto und am Highlight --
              ein Bauteil, kein zweiter Nachbau. */}
          <button
            type="button"
            className="blasenmenue__emoji blasenmenue__emoji--mehr"
            disabled={reaktionen.gesperrt}
            onClick={() => setAlle(true)}
            aria-label={t.chat.mehrEmoji}
            title={reaktionen.hinweis ?? t.chat.mehrEmoji}
          >
            ＋
          </button>
        </div>
      )}

      {/* Die volle Auswahl tritt an die Stelle von Reihe **und** Menü: sie
          ist so hoch, dass daneben ohnehin nichts mehr stünde. */}
      {reaktionen !== undefined && alle && (
        <div
          ref={menueRef}
          className="blasenmenue__voll"
          style={{
            left: platz?.menue.left ?? -9999,
            top: platz?.menue.top ?? -9999,
            visibility: platz === null ? 'hidden' : 'visible',
          }}
        >
          <Emojiwahl
            onPick={(emoji) => {
              onSchliessen()
              reaktionen.waehle(emoji)
            }}
            onZu={() => setAlle(false)}
          />
        </div>
      )}

      {!alle && (
        <div
          ref={menueRef}
          className="blasenmenue"
          role="menu"
          aria-label={t.chat.blasenmenue}
          style={{
            left: platz?.menue.left ?? -9999,
            top: platz?.menue.top ?? -9999,
            // Solange nicht gemessen ist, steht es außerhalb -- sichtbar wird
            // es erst an seinem Platz, sonst sähe man es springen.
            visibility: platz === null ? 'hidden' : 'visible',
          }}
        >
          {eintraege.map((eintrag) => (
            <button
              key={eintrag.name}
              type="button"
              role="menuitem"
              data-menue={eintrag.schluessel}
              className={
                eintrag.gefahr ? 'blasenmenue__zeile blasenmenue__zeile--gefahr' : 'blasenmenue__zeile'
              }
              onClick={() => {
                onSchliessen()
                eintrag.tun()
              }}
            >
              <span>{eintrag.name}</span>
              {eintrag.symbol}
            </button>
          ))}
        </div>
      )}
    </>,
    document.body,
  )
}
